<script context="module" lang="ts">
	export type SearchableBulletedListItem = {
		id: string;
		title: string;
		description?: string;
		sourceHref?: string;
		sourceLinkText?: string;
		linkText?: string;
		icon?: string;
		disabled?: boolean;
		loading?: boolean;
	};
</script>

<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { Button, Icon, Text } from '@Shelly/shelly-design-system';
	import type { TextPropsTypes } from '@Shelly/shelly-design-system';

	let className = '';
	export { className as class };
	export let list: SearchableBulletedListItem[] = [];
	export let searchHighlight = '';
	export let titleStyleType: TextPropsTypes['styleType'] = 'body';
	export let descriptionStyleType: TextPropsTypes['styleType'] = 'body-small';

	const dispatch = createEventDispatcher<{ linkClick: { id: string; index: string } }>();

	type HighlightParts = {
		before: string;
		match: string;
		after: string;
	};

	const getHighlightParts = (text: string, query: string): HighlightParts | null => {
		if (!query) return null;
		const startIndex = text.toLowerCase().indexOf(query.toLowerCase());
		if (startIndex === -1) return null;
		return {
			before: text.substring(0, startIndex),
			match: text.substring(startIndex, startIndex + query.length),
			after: text.substring(startIndex + query.length),
		};
	};

	const handleLinkClick = (item: SearchableBulletedListItem, index: number) => {
		if (item.disabled) return;
		dispatch('linkClick', { id: item.id, index: index.toString() });
	};
</script>

<ul class="bulleted-list {className}">
	{#each list as item, index (item.id)}
		{@const titleHighlight = getHighlightParts(item.title, searchHighlight)}
		{@const descriptionHighlight = getHighlightParts(item.description ?? '', searchHighlight)}
		<li>
			<Text
				class="d-inline-flex text-bold {item.description || item.sourceHref ? 'mb-xs' : ''}"
				styleType={titleStyleType}
			>
				{#if titleHighlight}
					{titleHighlight.before}<span class="text-primary text-bold"
						>{titleHighlight.match}</span
					>{titleHighlight.after}
				{:else}
					{item.title}
				{/if}
			</Text>
			{#if item.description || item.sourceHref}
				<Text
					class="text-color-muted {item.linkText ? 'mb-s' : ''}"
					styleType={descriptionStyleType}
				>
					<span class="mr-s">
						{#if item.description}
							{#if descriptionHighlight}
								{descriptionHighlight.before}<span class="text-primary text-bold"
									>{descriptionHighlight.match}</span
								>{descriptionHighlight.after}
							{:else}
								{item.description}
							{/if}
						{/if}
					</span>
					{#if item.sourceHref && item.sourceLinkText}
						<Text
							href={item.sourceHref}
							target="_blank"
							rel="noopener noreferrer"
							styleType={descriptionStyleType}
							class="link-underline"
						>
							<span class="d-inline-block">
								<Icon
									size="xs"
									icon="fa-regular fa-arrow-up-right-from-square"
									class="mr-xxs text-color-primary"
								/>
								{item.sourceLinkText}
							</span>
						</Text>
					{/if}
				</Text>
			{/if}
			{#if item.linkText}
				<Button
					size="s"
					icon={item.icon}
					variant="primary"
					disabled={item.disabled}
					loading={item.loading}
					aria-label={item.linkText}
					on:click={() => handleLinkClick(item, index)}
				>
					{item.linkText}
				</Button>
			{/if}
		</li>
	{/each}
</ul>

<style>
	.bulleted-list {
		display: flex;
		flex-direction: column;
		gap: 16px;
		margin: 0;
		padding-left: 24px;
	}
</style>
